import {
    addCustomEventListener,
    type BlockDefinition,
    type BlockId,
    BlockInstance,
    CustomEventListener,
    getNavigationDesktopLayout,
    type NavigationLayoutDesktop,
    PageRendererArgs,
    removeCustomEventListener,
} from "@softr-io/blocks-sdk";
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { getBlockParentIdMap } from "./block-root";
import { useDeepMemo } from "./useDeepMemo";
import { PageRef } from "./AppRoot";
import { isPreCompiledBlock } from "./PreCompiledBlockDefinition";
import { BlockProps, BlockRecordIdMap } from "@softr-io/blocks-sdk/react";
import { useCurrentRecord } from "./useCurrentRecord";
import { findFirstMappedDetailsBlock } from "./findFirstMappedDetailsBlock";

export type PageContextValue = {
    // Make sure that any new property added here is a stable reference.
    // This value is memoized in the Page component.
    // If this is not stable, it will cause the entire page to re-render on
    // every change, causing a noticeable slowdown on pages with >10 blocks
    setBlockInstanceRef: (blockId: string, ref: BlockInstance | null) => void;
    nestedBlocks: Record<BlockId, Record<BlockId, BlockDefinition>>;
    // All resource identifiers of the page.
    // This is used so the comments block can access the table-specific permissions.
    datasourceResourceIdentifiers: Map<string, Record<string, unknown>[]>;
    pageId: string;
    theme: PageRendererArgs["theme"];

    /**
     * Seems a bit ad-hoc, but this is used by the CustomCode block
     * to wait for block roots to appear in the DOM before executing.
     */
    topLevelDynamicBlockHrids: readonly string[];

    firstDetailsBlockHrid: string | null;

    blockIdToHrid: Record<string, string>;
    blockRecordIdMap: BlockRecordIdMap;
    currentRecord: BlockProps["currentRecord"];
    currentRecordDefinition: BlockDefinition | null;
    desktopNavLayout: NavigationLayoutDesktop | null;
};

const PageContext = createContext<PageContextValue | null>(null);
interface PageContextProviderProps {
    definitions: ReadonlyArray<BlockDefinition>;
    theme: PageRendererArgs["theme"];
    pageId: string;
    pageRef: React.Ref<PageRef>;
    nestedBlocks: Record<string, Record<string, BlockDefinition>>;
}

function useRefMap<T>() {
    const mapRef = useRef<Map<string, T> | null>(null);

    const getMap = () => {
        if (!mapRef.current) {
            mapRef.current = new Map();
        }
        return mapRef.current;
    };

    const setRefById = useCallback((id: string, ref: T | null) => {
        const map = getMap();
        if (ref) {
            map.set(id, ref);
        } else {
            map.delete(id);
        }
    }, []);

    return [getMap(), setRefById] as const;
}

export function PageContextProvider({ children, pageRef, ...props }: PropsWithChildren<PageContextProviderProps>) {
    const { definitions, pageId, theme: unstableTheme, nestedBlocks } = props;
    const theme = useDeepMemo(unstableTheme);

    const [blockInstanceMap, setBlockInstanceRef] = useRefMap<BlockInstance>();
    useImperativeHandle(pageRef, () => ({ blockInstanceMap }));

    type BlockDSResourceIds = Record<string, unknown>[];
    const dsResourceIdsEntries: Array<[blockId: string, BlockDSResourceIds]> = useDeepMemo(
        definitions.map((def) => [
            def.id,
            def.collection?.dataSources?.map((ds) => ds.resourceIdentifier.keys).flat() || [],
        ]),
    );

    // All resource identifiers of the page.
    // This is used so the comments block can access the table-specific permissions.
    const dsResourceIds = useMemo(
        () => new Map<string, BlockDSResourceIds>(dsResourceIdsEntries),
        [dsResourceIdsEntries],
    );
    const parentBlockIdMap = getBlockParentIdMap(definitions);
    const topLevelDynamicBlockHrids = useDeepMemo(
        definitions
            .filter((def) => !isPreCompiledBlock(def))
            .filter((def) => !parentBlockIdMap[def.id])
            .map((def) => def.hrid),
    );
    const firstDetailsBlock = findFirstMappedDetailsBlock(definitions);
    const firstDetailsBlockHrid = firstDetailsBlock?.hrid ?? null;
    const blockIdToHrid = useDeepMemo(Object.fromEntries(definitions.map((def) => [def.id, def.hrid])));
    const blockRecordIdMap = useBlockRecordIdMap({ definitions, pageId });
    const currentRecord = useCurrentRecord(definitions);
    const currentRecordDefinition = findFirstMappedDetailsBlock(definitions);
    const navBlock = definitions.find((def) => def.name === "navigation");
    const desktopNavLayout = navBlock ? getNavigationDesktopLayout(navBlock) : null;
    const pageCtx = useMemo(
        // careful with the memoization here.
        // Changing a single block definition should not dirty this context
        // otherwise any studio change will cause the entire page to re-render
        (): PageContextValue => ({
            pageId,
            setBlockInstanceRef,
            nestedBlocks,
            theme,
            datasourceResourceIdentifiers: dsResourceIds,
            topLevelDynamicBlockHrids,
            firstDetailsBlockHrid,
            blockIdToHrid,
            blockRecordIdMap,
            currentRecord,
            currentRecordDefinition,
            desktopNavLayout,
        }),
        [
            pageId,
            setBlockInstanceRef,
            nestedBlocks,
            theme,
            dsResourceIds,
            topLevelDynamicBlockHrids,
            firstDetailsBlockHrid,
            blockIdToHrid,
            blockRecordIdMap,
            currentRecord,
            currentRecordDefinition,
            desktopNavLayout,
        ],
    );
    return <PageContext.Provider value={pageCtx}>{children}</PageContext.Provider>;
}

export function usePageContext(): PageContextValue {
    const context = useContext(PageContext);
    if (!context) {
        throw new Error("usePageContext must be used within a PageContextProvider");
    }
    return context;
}

type PageId = string;
function useBlockRecordIdMap({
    definitions,
    pageId,
}: {
    definitions: readonly BlockDefinition[];
    pageId: string;
}): BlockRecordIdMap {
    const relevantBlockIds = useDeepMemo(
        definitions.filter((it) => it.name.toLowerCase().startsWith("list-details")).map((it) => it.id),
    );
    const [recordIdMap, setRecordIdMap] = useState<Record<PageId, BlockRecordIdMap | undefined>>(() => ({
        [pageId]: Object.fromEntries(relevantBlockIds.map((id) => [id, { recordId: null, isLoading: true }])),
    }));
    const blockIdToHrid = useDeepMemo(Object.fromEntries(definitions.map((def) => [def.id, def.hrid])));

    useEffect(() => {
        const cleanupFns: (() => void)[] = [];

        for (const id of relevantBlockIds) {
            const hrid = blockIdToHrid[id];
            if (!hrid) throw new Error("Block id to hrid mapping not found for block " + id);

            const listener: CustomEventListener<`get-record-${string}`> = (event) => {
                // In `dispatchEvent` we dispatch to both window and window.parent
                // so we must filter out events from "Open page" modals
                if (event.target !== window) return;
                setRecordIdMap((prev) => {
                    const prevPageMap = prev[pageId] || {};
                    // v4 blocks send an empty object instead of RecordItem when there is no record
                    if (!("id" in event.detail)) return prev;
                    if (prevPageMap[id]?.recordId !== event.detail.id || prevPageMap[id]?.isLoading) {
                        const newPageMap: BlockRecordIdMap = {
                            ...prevPageMap,
                            [id]: { isLoading: false, recordId: event.detail.id },
                        };
                        return {
                            ...prev,
                            [pageId]: newPageMap,
                        };
                    }

                    return prev;
                });
            };

            const itemDetailsHiddenListener: CustomEventListener<`item-details-hidden-${string}`> = () => {
                setRecordIdMap((prev) => {
                    const prevPageMap = prev[pageId] || {};

                    return {
                        ...prev,
                        [pageId]: {
                            ...prevPageMap,
                            [id]: { recordId: null, isLoading: false },
                        },
                    };
                });
            };

            addCustomEventListener(`get-record-${hrid}`, listener);
            addCustomEventListener(`item-details-hidden-${id}`, itemDetailsHiddenListener);
            cleanupFns.push(() => removeCustomEventListener(`get-record-${hrid}`, listener));
            cleanupFns.push(() => removeCustomEventListener(`item-details-hidden-${id}`, itemDetailsHiddenListener));
        }

        return () => {
            for (const cleanup of cleanupFns) {
                cleanup();
            }
        };
    }, [relevantBlockIds, blockIdToHrid, pageId]);

    return recordIdMap[pageId] ?? EMPTY_RECORD_ID_MAP;
}
const EMPTY_RECORD_ID_MAP: BlockRecordIdMap = {};
